import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Icon,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  TextField
} from '@material-ui/core';
import { updateQuoteProListingApi } from 'api';
import clsx from 'clsx';
import useStyles from '../styles';
import { calculateDiscountValueField } from './helpers/quoteDiscounts';
import { editDiscountValidationSchema } from './helpers/editDiscountValidationSchema';


const EditDiscount = ({open, onClose, editableOption}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { editableQuoteProduct } = useSelector((state: IReduxState) => state.quote);
  const [optionType, setOptionType] = useState(editableOption?.price_type);

  useEffect(() => {
    if (editableOption?.price_type) {
      setOptionType(editableOption.price_type);
    }
  }, [editableOption]);

  const existingQuoteproDiscounts = editableQuoteProduct?.custom_attributes?.find((attr) => attr.attribute_code === 'quotepro_discounts');
  const existingValues = existingQuoteproDiscounts?.value ? JSON.parse(existingQuoteproDiscounts.value) : [];

  const handleSaveDiscounts = (updatedData) => {
    if (existingValues) {
      const indexToEdit = existingValues.findIndex(
        (discount) => discount.price_type === editableOption.price_type && discount.price === editableOption.price
      );

      if (indexToEdit !== -1) {
        existingValues[indexToEdit] = { ...existingValues[indexToEdit], ...updatedData };

        const updatedStringifiedValues = JSON.stringify(existingValues);

        const editedProduct = {
          ...editableQuoteProduct,
          custom_attributes: [
            ...(editableQuoteProduct.custom_attributes || []),
            {
              attribute_code: 'quotepro_discounts',
              value: updatedStringifiedValues
            }
          ]
        };

        updateQuoteProListingApi({ dispatch, product: editedProduct });
      }
    }

    onClose(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={'sm'}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="edit-discount-options"
    >
      <DialogTitle className="flex justify-end p-1">
        <IconButton
          onClick={() => onClose(false)}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            ...editableOption
          }}
          enableReinitialize
          validationSchema={
            editDiscountValidationSchema(
              editableQuoteProduct?.price,
              existingValues
            )}
          onSubmit={handleSaveDiscounts}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <TableContainer className={classes.tableContainer}>
                <Typography className={classes.tableTitle}>
                  Discount
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Discount Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="align-top">
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          name="price_type"
                          SelectProps={{
                            placeholder: 'Discount Type'
                          }}
                          value={values.price_type}
                          onChange={(event) => {
                            const {value} = event.target;
                            const priceType = value === 'percent'
                              ? 'percent'
                              : 'fixed';
                            setOptionType(value === 'percent' ? 'percent' : 'fixed');
                            handleChange(event);
                            setFieldValue('price_type', priceType);
                            setFieldValue('price', values.price);
                          }}
                          onBlur={handleBlur}
                          error={touched.price_type && Boolean(errors.price_type)}
                          helperText={touched.price_type && errors.price_type}
                        >
                          <MenuItem value="percent">Percentage Off</MenuItem>
                          <MenuItem value="fixed">Dollar Off</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell className="align-top">
                        <NumberFormat
                          thousandSeparator
                          decimalScale={2}
                          fullWidth
                          suffix={values.price_type === 'percent' ? '%' : ''}
                          prefix={values.price_type !== 'percent' ? '$' : ''}
                          name="price"
                          value={Math.abs(values.price)}
                          variant="outlined"
                          onValueChange={(val) => setFieldValue(
                            'price',
                            val.floatValue ? val.floatValue : null
                          )}
                          customInput={TextField}
                          onBlur={handleBlur}
                          error={touched.price && Boolean(errors.price)}
                          helperText={touched.price && errors.price}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <div className={clsx('flex items-center pl-3', classes.textField)}>
                          {calculateDiscountValueField(optionType, editableQuoteProduct.price, values.price)}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <div className="flex items-center justify-end w-full my-6">
                <div>
                  <Button
                    variant="outlined"
                    onClick={() => onClose(false)}
                    className="mr-3"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditDiscount;
